import { kebabCase } from 'lodash'
import { useCallback, useMemo } from 'react'
import type { Address } from '@primitives/address.utils'
import { getStorageKey, type MigrationOptions, useStoredState } from '@ui/features/storage/useStoredState'
import type { VisibilityVariants } from '@ui/features/tables/visibility.types'
import { Duration } from '@ui/features/themes/design/0_primitives'
import { useCurrentDate } from '@ui/hooks/useCurrentDate'
import { defaultReleaseChannel, ReleaseChannel } from '@ui/lib/env'

function getFromLocalStorage<T>(storageKey: string) {
  const item = window.localStorage.getItem(storageKey)
  return (item && JSON.parse(item)) as T | null
}

const get = <T>(key: string, initialValue: T): T => getFromLocalStorage<T>(key) ?? initialValue

const set = <T>(storageKey: string, value: T) => {
  if (!localStorage) return console.warn(`No local storage available`) // can happen e.g. in iOS private mode
  if (value == null) {
    return window.localStorage.removeItem(storageKey)
  }
  window.localStorage.setItem(storageKey, JSON.stringify(value))
}

/**
 * A hook to use local storage with a key and an initial value.
 * Similar to useState, but persists the value in local storage.
 *
 * It is not exported, as we want to keep an overview of all the local storage keys used in the app.
 */
const useLocalStorage = <T>(key: string, initialValue: T, migration?: MigrationOptions<T>) =>
  useStoredState<T>({ key, initialValue, get, set, ...migration })

/* -- Export specific hooks so that we can keep an overview of all the local storage keys used in the app -- */
export const useShowTestNets = () => useLocalStorage<boolean>('showTestnets', false)

const [RELEASE_CHANNEL_KEY, RELEASE_CHANNEL_VERSION] = ['release-channel', 1] as const
export const getReleaseChannel = () =>
  getFromLocalStorage<ReleaseChannel>(getStorageKey(RELEASE_CHANNEL_KEY, RELEASE_CHANNEL_VERSION)) ??
  defaultReleaseChannel
export const useReleaseChannel = () =>
  useLocalStorage<ReleaseChannel>(RELEASE_CHANNEL_KEY, defaultReleaseChannel, {
    version: RELEASE_CHANNEL_VERSION,
    migrate: oldValue => (oldValue ? ReleaseChannel.Beta : ReleaseChannel.Stable),
    oldKey: 'beta',
  })

type RateType = 'borrow' | 'supply'

export const useShowNetRate = (type: RateType) =>
  useLocalStorage<Record<string, boolean>>(
    { borrow: 'showNetApr', supply: 'showNetApy' }[type],
    useMemo(() => ({}), []),
  )

export const useCreateLoanPreset = <T extends 'Safe' | 'MaxLtv' | 'Custom'>(defaultValue: T) =>
  useLocalStorage<T>('create-loan-preset', defaultValue)

export const useTableColumnVisibility = <Variant extends string, ColumnIds>(
  tableTitle: string,
  defaultVisibility: VisibilityVariants<Variant, ColumnIds>,
  migration: MigrationOptions<VisibilityVariants<Variant, ColumnIds>>,
) =>
  useLocalStorage<VisibilityVariants<Variant, ColumnIds>>(
    `table-column-visibility-${kebabCase(tableTitle)}`,
    defaultVisibility,
    migration,
  )

const EMPTY_COLUMN_OVERRIDES = {}

export const useTableColumnOverrides = (
  tableTitle: string,
  migration: MigrationOptions<Partial<Record<string, boolean>>>,
) =>
  useLocalStorage<Partial<Record<string, boolean>>>(
    `table-column-visibility-${kebabCase(tableTitle)}`,
    EMPTY_COLUMN_OVERRIDES,
    migration,
  )

const FAVORITE_MARKETS_KEY = 'favoriteMarkets'
export const getFavoriteMarkets = () => getFromLocalStorage<Address[]>(FAVORITE_MARKETS_KEY) ?? []
export const useFavoriteMarkets = () => {
  const initialValue = useMemo(() => [], [])
  return useLocalStorage<Address[]>(FAVORITE_MARKETS_KEY, initialValue)
}

export const useBandsChartVisible = () => useLocalStorage<boolean>('bands-chart-visible', false)

/**
 * Returns a tuple containing a boolean indicating whether the banner should be shown, and a function to dismiss the banner.
 * Do NOT export this hook, as it directly exposes local storage keys!
 */
const useDismissBanner = (bannerKey: string, frequency: keyof typeof Duration.Banner = 'Monthly') => {
  const [dismissedAt, setDismissedAt] = useLocalStorage<number | null>(bannerKey, null)
  const expirationTime = Duration.Banner[frequency]
  const date = useCurrentDate('1h') // minimum frequency is 1 day, so check every 1h while the window is open.
  const shouldShowBanner = dismissedAt == null || date.getTime() - dismissedAt >= expirationTime

  const dismissBanner = useCallback(() => setDismissedAt(Date.now()), [setDismissedAt])

  return [shouldShowBanner, dismissBanner] as const
}

export const useDismissDexDashboardRetirementBanner = () =>
  useDismissBanner('dex-dashboard-retirement-dismissed', 'Never')

export const useDismissAaveBanner = () => useDismissBanner('aave-v2-frozen-avalanche-polygon')
export const useDismissFantomRetirementBanner = () => useDismissBanner('fantom-chain-retirement', 'Daily')
export const useDismissCurveLiteBanner = (chainId: number) => useDismissBanner(`curve-lite-${chainId}`)
export const useDismissPhishingWarn = () => useDismissBanner('phishing-warning-dismissed')

export const useDismissPoolBanner = (network: string, poolAddress: Address) =>
  useDismissBanner(['pool-alert-banner-dismissed', network, poolAddress].join('-'), 'Daily')

export const useDismissMaintenanceModal = (dateISO: string | undefined) =>
  useLocalStorage<string | null>(`maintenance-modal-${dateISO}`, null)
export const useDismissMaintenanceBanner = (dateISO: string | undefined) =>
  useDismissBanner(`maintenance-banner-${dateISO}`, 'Daily')

export const usePinataJwt = () => useLocalStorage<string | undefined>('pinataJwt', undefined)
